import { Modal, Form, Select, DatePicker, Input, Descriptions, Alert, Button, message } from '../../ui';
import { useMutation } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { inr2 } from '../../lib/format';

const MODES = ['UPI', 'NEFT', 'RTGS', 'IMPS', 'CASH', 'CHEQUE', 'CARD', 'OTHER'];

/**
 * Record a REFUND of an advance whose order was cancelled/rejected. The platform only TRACKS a refund the
 * accountant already made outside it — attach the reversal proof. Posts the compensating ledger entry so the
 * retailer's balance nets to zero. Accounts/Admin/SA only (gated by PAYMENT.REFUND on the caller).
 */
export function RefundPaymentModal({ payment, orderNo, onClose, onDone }: {
  payment: any | null; orderNo?: string; onClose: () => void; onDone: () => void;
}) {
  const [form] = Form.useForm();
  const open = !!payment;

  const submit = useConfirmedMutation({ title: "Refund this payment?", content: "A reversing entry is posted to the retailer’s ledger. The original payment stays on record.", okText: "Yes, refund", danger: true }, {
    mutationFn: (v: any) => request<any>({
      url: `/payments/${payment.id}/refund`, method: 'POST',
      data: {
        refundMode: v.refundMode,
        refundDate: v.refundDate.format('YYYY-MM-DD'),
        refundReason: v.refundReason,
        refundProofUrls: v.refundProofUrls,
      },
    }),
    onSuccess: () => { message.success('Refund recorded'); onDone(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to record refund'),
  });

  /* EVERY file the original payment carried, not just the first. This is the evidence the person
     about to record a refund is meant to check against their bank, so handing them one of three
     slips and no sign that the others exist was the worst place to economise. */
  /* Same fallback the payments register uses: the multi-file column when it holds anything, the
     single legacy column otherwise — an EMPTY `attachmentUrls` must not hide a proof that only the
     old field carries. */
  const proofs: string[] = payment?.attachmentUrls?.length
    ? payment.attachmentUrls.filter(Boolean)
    : (payment?.attachmentUrl ? [payment.attachmentUrl] : []);
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={`Process refund — ${payment?.paymentNo ?? ''}`} width={620}>
      {payment && (
        <>
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Advance">{inr2(payment.amount)} · {payment.mode}</Descriptions.Item>
            <Descriptions.Item label="Order">{orderNo ?? payment.orderNo ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Original proof"><AttachmentCell files={proofs} label="Original payment proof" size={40} /></Descriptions.Item>
          </Descriptions>
          <Alert type="info" style={{ marginBottom: 12 }}
            title="Recording a refund you have already made outside the platform"
            description="Verify the original payment against your bank first. This captures the reversal + proof and returns the retailer's balance to zero — the platform does not move any money." />
          <Form key={payment.id} form={form} layout="vertical"
            initialValues={{ refundMode: payment.mode ?? 'UPI', refundDate: dayjs(), refundReason: undefined, refundProofUrls: undefined }}
            onFinish={(v) => submit.mutate(v)}>
            <Form.Item name="refundMode" label="Refund mode" rules={[{ required: true }]}>
              <Select options={MODES.map((m) => ({ value: m }))} />
            </Form.Item>
            <Form.Item name="refundDate" label="Refund date" rules={[{ required: true }]}>
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item name="refundReason" label="Reason / notes">
              <Input.TextArea rows={2} maxLength={255} placeholder="e.g. Order cancelled by retailer — advance returned via UPI" />
            </Form.Item>
            <Form.Item name="refundProofUrls" label="Refund proof"
              rules={[{ required: true, type: 'array', min: 1, message: 'Attach the reversal / transfer receipt' }]}
              extra="Required — the reversal screenshot or receipt.">
              <MultiFileUploadField category="receipt" buttonLabel="Upload refund proof" />
            </Form.Item>
            <Button variant="danger" type="submit" loading={submit.isPending} block>
              Record refund of {inr2(payment.amount)}
            </Button>
          </Form>
        </>
      )}
    </Modal>
  );
}
